import React from "react";
import GlobalStyles from "../styles/GlobalStyles";
import tw, { styled } from "twin.macro";
import "styled-components/macro";
import _ from "lodash";
import { useStaticQuery, graphql } from "gatsby";
import dawgsLogo from "../images/dawgs.png";

import Seo from "./Seo";

import Nav from "@shared/components/Nav";
import Scroll from "@shared/components/Scroll";
import SidePanel from "@shared/components/SidePanel";
import Footer from "@shared/components/Footer";
import SlideshowHero from "@shared/components/SlideshowHero";
import DynamicContainer from "@shared/components/DynamicContainer";
import Breadcrumbs from "@shared/components/Breadcrumbs";
import GroupBookings from "@shared/components/GroupBookings";
import { addNumberSuffix } from "@shared/utils";

import {
  BACKUP_LOGO_URL,
  facilityLinks,
  fanExperienceLinks,
  getTeamLinks,
  HOME_TEAMS,
  MAIN_COLOR,
  SITE,
  socialMediaData,
  ticketLinks,
  timelineAccount,
} from "../config";

const Main = styled.main`
  ${tw`relative min-h-screen`}
`;
const WhiteBanner = styled.div`
  ${tw`bg-white w-full h-8`}
`;
const Section = styled.section`
  ${tw`relative mx-auto min-h-[60vh] bg-[#EFEFEF]`}
  ${({ isHomePage }) => isHomePage && tw`bg-[#FFFFFF]`}
`;
const Container = styled(DynamicContainer)`
  ${tw`
    transition-[padding,gap]
    grid grid-cols-1
    py-4 sm:py-8
  `}
  ${({ isHomePage }) =>
    isHomePage
      ? tw`lg:grid-cols-[minmax(0,2fr),minmax(300px, 1fr)] gap-8 lg:gap-16 xl:gap-24 2xl:gap-32`
      : tw`lg:grid-cols-[minmax(0,3fr),minmax(0, 1fr)] gap-8 pt-0 sm:pt-0`}
`;

const ContentMain = styled.div`
  ${tw`bg-white w-full overflow-hidden `}
  ${({ redBg }) => redBg && tw`bg-[#EC1F27] text-white`}

  ${({ noBg }) => noBg && tw`bg-transparent`}
`;

const ContentSide = styled.div`
  ${tw`w-full`}
`;

const Layout = ({
  seoTitle,
  seoDescription,
  seoTitlePrefix,
  children,
  noBg,
  redBg,
  location,
  pageTitle,
  useSeoForTitle,
  isHomePage = false,
}) => {
  const data = useStaticQuery(graphql`
    query LayoutQuery {
      # only homepage
      markdownRemark(frontmatter: { templateKey: { eq: "index-page" } }) {
        frontmatter {
          seo {
            title
            description
          }
          navText
          WCBLLink
          hero {
            watchLiveLink
            teamSeasonLink
            slides {
              image {
                childImageSharp {
                  gatsbyImageData(layout: FULL_WIDTH)
                }
              }
              dots {
                enabled
                size
                color
              }
              overlay {
                enabled
                color
                opacity
              }
              video {
                publicURL
              }
              headers
              blurb
              buttons {
                icon
                label
                url
                color
                width
              }
              cutout {
                image {
                  childImageSharp {
                    gatsbyImageData(layout: FULL_WIDTH)
                  }
                }
                position
                offset
              }
            }
          }
          groupBookings {
            slideshowHeader
            header
            subHeader
            description
            subDescription
            phone
            email
            ticketLink
            tourLink
            list {
              image {
                childImageSharp {
                  gatsbyImageData
                }
              }
              title
              groupSize
              location
              ticketLink
              price
              location2
            }
          }
        }
      }
      # only home page
      allHomeGames: allMarkdownRemark(
        filter: {
          frontmatter: {
            templateKey: { eq: "game-post" }
            homeTeam: { in: ["Dawgs", "dawgs"] }
          }
        }
        sort: { fields: frontmatter___date, order: ASC }
      ) {
        nodes {
          id
          frontmatter {
            homeTeam
            awayTeam
            date
            ticketLink
            yr
            tv
          }
        }
      }

      latestRosterPage: allSitePage(
        filter: { path: { regex: "/^/roster/[0-9]{4}$/" } }
        sort: { fields: path, order: DESC }
        limit: 1
      ) {
        nodes {
          path
          id
        }
      }
      latestSchedulePage: allSitePage(
        filter: { path: { regex: "/^/schedule/[0-9]{4}$/" } }
        sort: { fields: path, order: DESC }
        limit: 1
      ) {
        nodes {
          path
          id
        }
      }
      # only homepage
      allGames: allMarkdownRemark(
        filter: { frontmatter: { templateKey: { eq: "game-post" } } }
        sort: { fields: frontmatter___date, order: ASC }
      ) {
        nodes {
          fields {
            slug
          }
          id
          frontmatter {
            title
            homeTeam
            awayTeam
            yr
            date
            ticketLink
            liveScoreLink
            tv
            stats {
              inningStats {
                home
                away
              }
            }
          }
        }
      }
      # only homepage
      allTeams: allMarkdownRemark(
        filter: { frontmatter: { mdType: { eq: "team" } } }
      ) {
        edges {
          node {
            frontmatter {
              name
              location {
                fieldName
                town
                abbreviation
              }
              logo {
                childImageSharp {
                  gatsbyImageData(layout: FULL_WIDTH)
                }
              }
            }
          }
        }
      }
      # only homepage
      allEvents: allMarkdownRemark(
        filter: { frontmatter: { mdType: { eq: "event" } } }
        sort: { order: ASC, fields: frontmatter___startDate }
      ) {
        edges {
          node {
            frontmatter {
              mdType
              name
              startDate
              endDate
              highlightColor
            }
          }
        }
      }
      # not on homepage
      recentNews: allMarkdownRemark(
        filter: {
          frontmatter: {
            templateKey: { eq: "news-post" }
            publish: { eq: true }
          }
        }
        sort: { fields: frontmatter___date, order: DESC }
        limit: 3
      ) {
        nodes {
          id
          fields {
            slug
          }
          frontmatter {
            title
            hero {
              pageImage {
                childImageSharp {
                  gatsbyImageData(aspectRatio: 1, layout: FULL_WIDTH)
                }
              }
            }
            date
          }
          excerpt(pruneLength: 80)
        }
      }

      # rosterHistoryPages: allSitePage(
      #   filter: { path: { regex: "/^/roster/[0-9]{4}$/" } }
      #   sort: { fields: path, order: ASC }
      # ) {
      #   nodes {
      #     path
      #     id
      #   }
      # }

      # scheduleHistoryPages: allSitePage(
      #   filter: { path: { regex: "/^/schedule/[0-9]{4}$/" } }
      #   sort: { fields: path, order: ASC }
      # ) {
      #   nodes {
      #     path
      #     id
      #   }
      # }

      site {
        siteMetadata {
          title
          description
        }
      }
      # allInstaNode(sort: { fields: timestamp, order: DESC }, limit: 3) {
      #   edges {
      #     node {
      #       id
      #       caption
      #       timestamp
      #       likes
      #       comments
      #       mediaType
      #       localFile {
      #         childImageSharp {
      #           gatsbyImageData(width: 300, placeholder: BLURRED)
      #         }
      #       }
      #     }
      #   }
      # }
    }
  `);
  addNumberSuffix(data.allGames.nodes);

  return (
    <>
      <GlobalStyles />
      <Seo
        title={seoTitle}
        description={seoDescription}
        titlePrefix={seoTitlePrefix}
        isHomePage={isHomePage}
      />
      <Main>
        <Nav
          isHomePage={isHomePage}
          data={data}
          color={MAIN_COLOR}
          logoSrc={dawgsLogo}
          site={SITE}
          homeTeams={HOME_TEAMS}
          getNavData={NAV_DATA}
          socialMediaData={socialMediaData}
        />
        {isHomePage ? (
          <SlideshowHero
            data={data}
            site={SITE}
            color={MAIN_COLOR}
            height={725}
          />
        ) : (
          <WhiteBanner />
        )}
        <Section isHomePage={isHomePage}>
          {!isHomePage && (
            <Breadcrumbs
              seoTitle={seoTitle}
              pageTitle={pageTitle}
              location={location}
              useSeoForTitle={useSeoForTitle}
              site={SITE}
            />
          )}
          <Container isHomePage={isHomePage}>
            <ContentMain noBg={noBg} redBg={redBg}>
              {children}
            </ContentMain>
            <ContentSide>
              <SidePanel
                isHomePage={isHomePage}
                location={location}
                data={data}
                color={MAIN_COLOR}
                logoUrl={BACKUP_LOGO_URL}
                site={SITE}
                // timelineAccount={timelineAccount}
                homeTeams={HOME_TEAMS}
              />
            </ContentSide>
          </Container>
        </Section>
        {isHomePage && <GroupBookings data={data} />}
        <Footer
          data={data}
          color={MAIN_COLOR}
          ticketLinks={ticketLinks}
          getTeamLinks={getTeamLinks}
          facilityLinks={facilityLinks}
          fanExperienceLinks={fanExperienceLinks}
          socialMediaData={socialMediaData}
        />
      </Main>
      <Scroll showBelow={200} />
    </>
  );
};

export default Layout;

const NAV_DATA = (rosterPagePath, links = {}) => ({
  team: {
    links: [
      {
        label: "Single Game Tickets",
        to: "/tickets/single-game",
      },
      {
        label: "Season Tickets",
        to: "/tickets/season-tickets",
      },
      {
        label: "Group & Patio Bookings",
        to: "/tickets/group-bookings",
      },
      {
        label: "Legacy Seats",
        to: "/tickets/legacy-seats",
      },
      {
        label: "WCBL All Star Game",
        to: _.get(links, "WCBLLink", ""),
        external: true,
      },
      {
        label: "Concerts at Seaman Stadium",
        to: "/tickets/concerts",
      },
      // {
      //   label: "Rain Out & Cancellation Policy",
      //   to: "/tickets/rain-out-and-cancellation",
      // },
    ],
  },
  teamInfo: {
    links: [
      {
        label: "Roster & Coaching Staff",
        to: rosterPagePath,
      },
      {
        label: "Front Office",
        to: "/front-office",
      },
      {
        label: "Statistics",
        to: "http://baseball.pointstreak.com/team_stats.html?teamid=155849",
        external: true,
      },
      {
        label: "News",
        to: "/news",
      },
      {
        label: "History",
        to: "/history",
      },
      {
        label: "Hall of Fame",
        to: "/hall-of-fame",
      },
      {
        label: "Dawgs MLB Draft Picks",
        to: "/dawgs-mlb-draft-picks",
      },
      {
        label: "Player Application",
        to: "/player-application",
      },
      {
        label: "Become A Host Family",
        to: "/become-a-host-family",
      },
      {
        label: "Dawgs Baseball Camp",
        to: "/dawgs-baseball-camp",
      },
      {
        label: "Diggity And Player Appearances",
        to: "/diggity-and-player-appearances",
      },
      {
        label: "Volunteer At The Ballpark",
        to: "/volunteer-at-the-ballpark",
      },
      {
        label: "Dawgs 50/50",
        to: "/dawgs-50-50",
      },
    ],
  },
  facilities: {
    links: [
      {
        label: "Seaman Stadium",
        to: "/facilities/seaman-stadium",
      },
      {
        label: "Kore 4 (+14) Corner",
        to: "/facilities/kore-4-14-corner",
      },
      {
        label: "Ircandia Outdoor Training Centre",
        to: "/facilities/ircandia-outdoor-training-centre",
      },
      {
        label: "Duvernay Fieldhouse",
        to: "/facilities/duvernay-fieldhouse",
      },
      {
        label: "Tourmaline Field",
        to: "/facilities/tourmaline-field",
      },
      {
        label: "Conrad Field",
        to: "/facilities/conrad-field",
      },
    ],
  },
  "fan-experience": {
    links: [
      {
        label: "Food and Beverage menu",
        to: "/fan-experience/food-and-beverage-menu",
      },
      {
        label: "Rules of the ballpark",
        to: "/fan-experience/rules-of-the-ballpark",
      },
      {
        label: "Stadium Map",
        to: "/fan-experience/stadium-map",
      },
      {
        label: "FAQ",
        to: "/fan-experience/FAQ",
      },
      {
        label: "Distance/Directions",
        to: "/fan-experience/distance-directions",
      },
      {
        label: "Promo Schedule",
        to: "/fan-experience/promo-schedule",
      },
    ],
  },
});
